import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { AdminNav } from "@/components/AdminNav";
import { extractDocumentText } from "@/lib/resumeSanitizer";
import { scrubText } from "@/lib/resumeScrubber";

/*
 * Admin Panel — Privacy audit (2026-10-28).
 *
 * Choose a folder of fictional test résumés (.docx / .pdf) with labels.csv, the
 * answer key (file, expect = remove / keep, category, text). This browser reads
 * each résumé and runs the upload page's privacy scan; the server runs its own
 * privacy step on the result and returns a zip: report.md, results.csv and the
 * scanned texts. Nothing is stored. Test data only. Backed by
 * /api/admin/privacy-audit, which exists only when the server has
 * BULK_AUDIT_ENABLED on (staging).
 */

const MAX_RESUMES = 300;

async function blobError(ex) {
  // The request asks for a zip, so an error body arrives as a Blob.
  const data = ex?.response?.data;
  if (data instanceof Blob) {
    try {
      const detail = JSON.parse(await data.text())?.detail;
      if (detail) return typeof detail === "string" ? detail : JSON.stringify(detail);
    } catch { /* not JSON */ }
  }
  return formatApiErrorDetail(ex);
}

function saveBlob(blob, disposition, fallback) {
  const match = (disposition || "").match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminPrivacyAudit() {
  const inputRef = useRef(null);
  const [labels, setLabels] = useState(null); // File
  const [resumes, setResumes] = useState([]); // File[]
  const [name, setName] = useState("");
  const [progress, setProgress] = useState(null); // {done, total}
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    // React has no prop for folder selection; set the attributes directly.
    if (inputRef.current) {
      inputRef.current.setAttribute("webkitdirectory", "");
      inputRef.current.setAttribute("directory", "");
    }
  }, []);

  const onFolder = (e) => {
    const files = Array.from(e.target.files || []);
    setLabels(files.find((f) => f.name.toLowerCase() === "labels.csv") || null);
    setResumes(files.filter((f) => /\.(docx|pdf)$/i.test(f.name)).sort((a, b) => a.name.localeCompare(b.name)));
    const root = (files[0]?.webkitRelativePath || "").split("/")[0];
    setName(root || "");
  };

  const run = async () => {
    if (!labels || !resumes.length || resumes.length > MAX_RESUMES) return;
    setProgress({ done: 0, total: resumes.length });
    try {
      const items = [];
      for (let i = 0; i < resumes.length; i++) {
        const file = resumes[i];
        try {
          const extracted = await extractDocumentText(file);
          items.push({ file: file.name, extracted_text: extracted, browser_text: scrubText(extracted) });
        } catch (ex) {
          items.push({ file: file.name, error: ex?.code || ex?.message || "could not read the file" });
        }
        setProgress({ done: i + 1, total: resumes.length });
      }
      const r = await api.post(
        "/admin/privacy-audit",
        { name, labels_csv: await labels.text(), resumes: items },
        { responseType: "blob" },
      );
      saveBlob(r.data, r.headers?.["content-disposition"], "privacy-audit.zip");
      toast.success("Privacy audit downloaded");
    } catch (ex) {
      if (ex?.response?.status === 404) setDisabled(true);
      else toast.error(await blobError(ex));
    } finally {
      setProgress(null);
    }
  };

  const card = "rounded-2xl border border-[color:var(--border-stone)] bg-white p-6 text-sm space-y-3";
  return (
    <div className="min-h-screen w-full bg-[color:var(--bg-base)]" data-testid="admin-privacy-audit">
      <AdminNav title="Privacy Audit" />
      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {disabled ? (
          <div className={card} data-testid="privacy-audit-disabled">
            The privacy audit is switched off on this server. It runs on staging only, when the backend has
            <code className="mx-1">BULK_AUDIT_ENABLED=true</code>.
          </div>
        ) : (
          <div className={card}>
            <p>
              Choose a folder of <strong>fictional</strong> test résumés (.docx or .pdf) with <code>labels.csv</code>:
              one row per detail that must be removed and per phrase that must be kept
              (columns <code>file, expect, category, text</code>). Each résumé goes through this browser&apos;s
              privacy scan and then the server&apos;s, and you download a zip with the report.
            </p>
            <input ref={inputRef} type="file" multiple onChange={onFolder} className="text-xs" data-testid="privacy-folder-input" />
            {(labels || resumes.length > 0) && (
              <ul className="text-xs space-y-1" data-testid="privacy-folder-summary">
                <li>{resumes.length} résumé file(s){resumes.length > MAX_RESUMES ? ` — at most ${MAX_RESUMES}` : ""}</li>
                <li>{labels ? "labels.csv found" : "labels.csv is missing"}</li>
              </ul>
            )}
            <div className="flex items-center gap-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Audit name"
                className="w-64 rounded border border-[color:var(--border-stone)] px-2 py-1 text-xs"
                aria-label="Audit name"
              />
              <button
                type="button"
                onClick={run}
                disabled={!labels || !resumes.length || resumes.length > MAX_RESUMES || !!progress}
                className="rounded-full border px-4 py-1.5 text-xs font-medium disabled:opacity-50"
                data-testid="privacy-run"
              >
                {progress ? `Scanning ${progress.done} of ${progress.total}…` : "Run privacy audit"}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
